import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { ICategoryDataSummaryModel } from "../../types/types";
import { getShortMonthName } from "../../utils/common";
import {
  Box,
  IconButton,
  Tooltip as MuiTooltip,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  HelpOutline as HelpIcon,
  FullscreenExit as FullscreenExitIcon,
} from "@mui/icons-material";
import { useDataExplanation } from "../../hooks/useDataExplanation";
import { DataExplanationDialog } from "../Dialogs/DataExplanationDialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import theme from "../../styles/theme";
import { getColorForId } from "../../utils/chartColors";
import { chartYearLabel, useChartYears } from "../../hooks/usePipeline";

// Helper function to get optimal x-axis configuration based on data length and expanded state
const getOptimalXAxisConfig = (
  dataLength: number,
  timePeriod: string,
  isExpanded: boolean = false,
) => {
  const baseConfig = {
    fontSize: 14,
    angle: 0,
    textAnchor: "middle" as const,
    height: 40, // Reduced from 60
    interval: 0,
  };

  // In expanded view, we can show more data points before applying optimizations
  const threshold = isExpanded
    ? {
        small: 12, // Show all up to 12 points
        medium: 24, // Show every other up to 24 points
        large: 48, // Start rotating at 48 points
        xlarge: 72, // Very large datasets
      }
    : {
        small: 6, // Show all up to 6 points
        medium: 12, // Show every other up to 12 points
        large: 18, // Start rotating at 18 points
        xlarge: 36, // Very large datasets
      };

  if (dataLength <= threshold.small) return baseConfig;

  if (dataLength <= threshold.medium) {
    return { ...baseConfig, interval: 1 };
  }

  if (dataLength <= threshold.large) {
    return {
      ...baseConfig,
      fontSize: isExpanded ? 13 : 12,
      angle: -30,
      textAnchor: "end" as const,
      height: 50, // Reduced from 70
      interval: isExpanded ? 1 : 1,
    };
  }

  if (dataLength <= threshold.xlarge) {
    return {
      ...baseConfig,
      fontSize: isExpanded ? 12 : 11,
      angle: -45,
      textAnchor: "end" as const,
      height: 60, // Reduced from 80
      interval: isExpanded
        ? Math.floor(dataLength / 16)
        : Math.floor(dataLength / 12),
    };
  }

  // For very large datasets
  return {
    ...baseConfig,
    fontSize: isExpanded ? 11 : 10,
    angle: -45,
    textAnchor: "end" as const,
    height: 70, // Reduced from 90
    interval: isExpanded
      ? Math.floor(dataLength / 12)
      : Math.floor(dataLength / 8),
  };
};

// Helper function to create compact labels for congested charts
const getCompactLabel = (
  displayPeriod: string,
  timePeriod: string,
  dataLength: number,
  isExpanded: boolean = false,
): string => {
  // In expanded view, show full labels for more data points
  const threshold = isExpanded ? 24 : 12;
  if (dataLength <= threshold) return displayPeriod;

  if (timePeriod === "month") {
    // "2024 Jan" -> "Jan '24"
    const match = displayPeriod.match(/(\d{4})\s+(\w{3})/);
    if (match) return `${match[2]} '${match[1].slice(-2)}`;
  } else if (timePeriod === "quarter") {
    // "2024 Q1" -> "Q1 '24"
    const match = displayPeriod.match(/(\d{4})\s+(Q\d)/);
    if (match) return `${match[2]} '${match[1].slice(-2)}`;
  }

  return displayPeriod;
};

// Unique variables to chart. Average Price only accounts for Seller & Buyer
// transactions, so variables without any S&B transactions are left out.
const getVisibleVariables = (
  categoryData: ICategoryDataSummaryModel[],
  mode: "count" | "amount" | "average",
): { id: string; name: string }[] => {
  const sbCounts = new Map<string, number>();
  categoryData.forEach((item) => {
    const id = String(item.id);
    sbCounts.set(id, (sbCounts.get(id) || 0) + (item.sbCount || 0));
  });

  return categoryData.reduce(
    (acc: { id: string; name: string }[], item) => {
      const id = String(item.id);
      if (mode === "average" && !sbCounts.get(id)) return acc;
      if (!acc.some((i) => i.id === id)) {
        acc.push({ id, name: item.name || "" });
      }
      return acc;
    },
    [],
  );
};

interface AllVariablesTrendChartProps {
  category: string;
  selectedYearIds?: number[];
  mode: "count" | "amount" | "average";
  timePeriod: "quarter" | "month" | "year";
  expandTrigger?: boolean;
}

export const AllVariablesTrendChart: React.FC<AllVariablesTrendChartProps> = ({
  category,
  selectedYearIds,
  mode,
  timePeriod,
  expandTrigger,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const years = useChartYears(selectedYearIds);
  const { categories } = useSelector((state: RootState) => state.analytics);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  React.useEffect(() => {
    if (expandTrigger) {
      setIsExpanded(true);
    }
  }, [expandTrigger]);
  const categoryData = categories[category]?.data;
  const isSingleYearSelected = selectedYearIds?.length === 1;

  const {
    explainData,
    explanation,
    isLoading,
    isOpen,
    closeExplanation,
    dailyLimitInfo,
  } = useDataExplanation({
    onError: (error) => console.error("Data explanation error:", error),
  });

  // Generate complete set of periods for selected years - memoized to react to prop changes
  const allPeriods = useMemo(() => {
    if (timePeriod === "year" || !isSingleYearSelected) {
      if (timePeriod === "year") {
        return (selectedYearIds ?? []).length > 0
          ? selectedYearIds!
          : years.map((year) => year.id);
      } else if (timePeriod === "quarter") {
        // Generate all quarters for selected years
        const yearIds =
          (selectedYearIds ?? []).length > 0
            ? selectedYearIds!.filter((id): id is number => id !== undefined)
            : years
                .map((year) => year.id)
                .filter((id): id is number => id !== undefined);

        const periods: Array<{
          yearId: number;
          quarter: number;
          periodKey: string;
        }> = [];
        yearIds.forEach((yearId) => {
          for (let q = 1; q <= 4; q++) {
            const year = chartYearLabel(years.find((y) => y.id === yearId)?.year);
            if (year) {
              periods.push({
                yearId,
                quarter: q,
                periodKey: `${year} Q${q}`,
              });
            }
          }
        });
        return periods;
      } else if (timePeriod === "month") {
        // Generate all months for selected years
        const yearIds =
          (selectedYearIds ?? []).length > 0
            ? selectedYearIds!.filter((id): id is number => id !== undefined)
            : years
                .map((year) => year.id)
                .filter((id): id is number => id !== undefined);

        const periods: Array<{
          yearId: number;
          month: number;
          periodKey: string;
        }> = [];
        yearIds.forEach((yearId) => {
          for (let m = 1; m <= 12; m++) {
            const year = chartYearLabel(years.find((y) => y.id === yearId)?.year);
            if (year) {
              periods.push({
                yearId,
                month: m,
                periodKey: `${year} ${getShortMonthName(m)}`,
              });
            }
          }
        });
        return periods;
      }
    }

    // Single year behavior remains the same
    return Array.from(
      { length: timePeriod === "month" ? 12 : 4 },
      (_, i) => i + 1,
    );
  }, [timePeriod, isSingleYearSelected, selectedYearIds, years]);

  const [chartData, setChartData] = useState<any[]>([]);

  useEffect(() => {
    if (!categoryData || categoryData.length === 0) {
      setChartData([]);
      return;
    }

    const uniqueVariables = getVisibleVariables(categoryData, mode);
    const visibleIds = new Set(uniqueVariables.map((v) => v.id));

    const transformData = () => {
      const groupedByPeriod: Record<string | number, Record<string, any>> = {};
      const variableNames = uniqueVariables.map((v) => v.name);

      // Initialize periods based on the new allPeriods structure
      if (isSingleYearSelected && timePeriod !== "year") {
        // Single year with quarters/months
        (allPeriods as number[]).forEach((period) => {
          const periodKey = String(period);
          const periodObj: Record<string, any> = {
            period,
            displayPeriod:
              timePeriod === "quarter"
                ? `Q${period}`
                : getShortMonthName(period),
          };

          variableNames.forEach((name) => {
            if (name) {
              periodObj[name] = 0;
            }
          });

          groupedByPeriod[periodKey] = periodObj;
        });
      } else if (timePeriod === "quarter" || timePeriod === "month") {
        // Multi-year quarters/months
        (
          allPeriods as Array<{
            yearId: number;
            quarter?: number;
            month?: number;
            periodKey: string;
          }>
        ).forEach((period) => {
          const periodKey = `${period.yearId}-${
            timePeriod === "quarter" ? period.quarter : period.month
          }`;
          const periodObj: Record<string, any> = {
            period: periodKey,
            displayPeriod: period.periodKey,
            yearId: period.yearId,
            [timePeriod]:
              timePeriod === "quarter" ? period.quarter : period.month,
          };

          variableNames.forEach((name) => {
            if (name) {
              periodObj[name] = 0;
            }
          });

          groupedByPeriod[periodKey] = periodObj;
        });
      } else {
        // Year view
        (allPeriods as number[]).forEach((period) => {
          const periodKey = String(period);
          const year = chartYearLabel(years.find((y) => y.id === period)?.year);
          const periodObj: Record<string, any> = {
            period,
            displayPeriod: year ? `${year}` : `${period}`,
          };

          variableNames.forEach((name) => {
            if (name) {
              periodObj[name] = 0;
            }
          });

          groupedByPeriod[periodKey] = periodObj;
        });
      }

      categoryData.forEach((item: ICategoryDataSummaryModel) => {
        let periodKey: string;

        if (isSingleYearSelected && timePeriod !== "year") {
          // For single year, use quarter or month from the item
          periodKey = String(
            timePeriod === "quarter" ? item.quarter : item.month,
          );
        } else if (timePeriod === "quarter" || timePeriod === "month") {
          // For multi-year quarters/months, use yearId and period
          periodKey = `${item.yearId}-${
            timePeriod === "quarter" ? item.quarter : item.month
          }`;
        } else {
          // For year view, use year ID
          periodKey = String(item.yearId);
        }

        if (!periodKey || !groupedByPeriod[periodKey]) {
          return;
        }

        const variableName = item.name;
        if (!variableName || !visibleIds.has(String(item.id))) return;

        // Average Price only accounts for Seller & Buyer transactions
        const count =
          (mode === "average" ? item.sbCount : item.totalCount) || 0;
        const amount =
          (mode === "average" ? item.sbAmount : item.totalAmount) || 0;

        // Get existing values to accumulate instead of overwriting
        const existingCount =
          groupedByPeriod[periodKey][`${variableName}_count`] || 0;
        const existingAmount =
          groupedByPeriod[periodKey][`${variableName}_amount`] || 0;

        // Store intermediate totals for proper averaging calculation
        groupedByPeriod[periodKey][`${variableName}_count`] =
          existingCount + count;
        groupedByPeriod[periodKey][`${variableName}_amount`] =
          existingAmount + amount;

        // Calculate the display value based on mode
        const totalCount = groupedByPeriod[periodKey][`${variableName}_count`];
        const totalAmount =
          groupedByPeriod[periodKey][`${variableName}_amount`];

        groupedByPeriod[periodKey][variableName] =
          mode === "count"
            ? totalCount
            : mode === "amount"
              ? totalAmount
              : totalCount > 0
                ? totalAmount / totalCount
                : 0;
      });

      return Object.values(groupedByPeriod)
        .map((period: any) => {
          // Clean up intermediate aggregation properties
          const cleanPeriod = { ...period };
          Object.keys(cleanPeriod).forEach((key) => {
            if (key.endsWith("_count") || key.endsWith("_amount")) {
              delete cleanPeriod[key];
            }
          });
          return cleanPeriod;
        })
        .sort((a, b) => {
          if (isSingleYearSelected && timePeriod !== "year") {
            // Single year: sort by period number
            const periodA = Number(a.period) || 0;
            const periodB = Number(b.period) || 0;
            return periodA - periodB;
          } else if (timePeriod === "quarter" || timePeriod === "month") {
            // Multi-year quarters/months: sort by year then by period
            const yearA = a.yearId || 0;
            const yearB = b.yearId || 0;
            if (yearA !== yearB) {
              const actualYearA = years.find((y) => y.id === yearA)?.year || 0;
              const actualYearB = years.find((y) => y.id === yearB)?.year || 0;
              return actualYearA - actualYearB;
            }
            const periodA = a[timePeriod] || 0;
            const periodB = b[timePeriod] || 0;
            return periodA - periodB;
          } else {
            // Year view: sort by year
            const periodA = Number(a.period) || 0;
            const periodB = Number(b.period) || 0;
            const yearA = years.find((y) => y.id === periodA)?.year || 0;
            const yearB = years.find((y) => y.id === periodB)?.year || 0;
            return yearA - yearB;
          }
        });
    };

    const data = transformData();
    setChartData(data);
  }, [
    categoryData,
    allPeriods,
    isSingleYearSelected,
    timePeriod,
    mode,
    years,
    selectedYearIds,
  ]);

  if (!categoryData || categoryData.length === 0) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        No data available for this category.
      </div>
    );
  }

  const uniqueVariables = getVisibleVariables(categoryData, mode);

  const handleExplainData = () => {
    const selectedYearText =
      selectedYearIds && selectedYearIds.length > 0
        ? selectedYearIds
            .map((id) => chartYearLabel(years.find((y) => y.id === id)?.year))
            .join(", ")
        : "all years";

    const variableNames = uniqueVariables.map((v) => v.name).join(", ");

    explainData(
      chartData,
      "category",
      mode,
      timePeriod,
      selectedYearText,
      category,
      undefined,
      variableNames,
    );
  };

  const xAxisConfig = getOptimalXAxisConfig(
    chartData.length,
    timePeriod,
    false,
  );

  return (
    <>
      <div
        style={{
          position: "relative",
          minWidth: isMobile ? "70vw" : "500px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: -30,
            zIndex: 1,
            display: "flex",
            gap: 2,
            alignItems: "center",
          }}
        >
          <MuiTooltip title="Get AI insights about this trend data">
            <IconButton
              size="small"
              onClick={handleExplainData}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              <HelpIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>
        </Box>
        <ResponsiveContainer width="100%" height={isMobile ? 350 : 450}>
          <LineChart
            data={chartData}
            margin={{
              top: 5,
              right: xAxisConfig.angle !== 0 ? 40 : 25,
              left: isMobile
                ? 1
                : mode === "amount" || mode === "average"
                  ? 50
                  : 35,

              bottom: isMobile ? 40 : xAxisConfig.height,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="displayPeriod"
              axisLine={false}
              tick={{
                fontSize: isMobile ? 12 : xAxisConfig.fontSize,
                fontFamily: "Segoe UI",
              }}
              tickLine={false}
              type="category"
              angle={isMobile ? -45 : xAxisConfig.angle}
              textAnchor={isMobile ? "end" : xAxisConfig.textAnchor}
              height={isMobile ? 40 : xAxisConfig.height}
              interval={xAxisConfig.interval}
              //  interval={isMobile ? Math.max(1, Math.floor(chartData.length / 4)) : xAxisConfig.interval}

              tickFormatter={(value) =>
                getCompactLabel(value, timePeriod, chartData.length, false)
              }
            />
            <YAxis
              allowDecimals={mode === "count" ? false : true}
              tickFormatter={(value) => {
                if (mode === "amount" || mode === "average") {
                  // Format large numbers with abbreviations
                  const absValue = Math.abs(value);
                  if (absValue >= 1000000) {
                    return `$${(value / 1000000).toFixed(1)}M`;
                  } else if (absValue >= 1000) {
                    return `$${(value / 1000).toFixed(1)}K`;
                  } else {
                    return new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(value);
                  }
                }
                return value;
              }}
            />
            <Tooltip
              formatter={(value: number | string, name, props) => {
                if (mode === "amount" || mode === "average") {
                  return [
                    new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(value as number),
                    name,
                  ];
                }
                return [value, name];
              }}
              labelFormatter={(label) => {
                // Use the displayPeriod from the data item directly
                const dataItem = chartData.find(
                  (item) =>
                    item.period === label ||
                    item.displayPeriod === label ||
                    String(item.period) === String(label),
                );

                if (dataItem && dataItem.displayPeriod) {
                  return dataItem.displayPeriod;
                }

                // Fallback to original logic with better error handling
                if (timePeriod === "year") {
                  const year = chartYearLabel(years.find((y) => y.id === Number(label))?.year);
                  return year ? `${year}` : `${label}`;
                } else if (isSingleYearSelected) {
                  const numLabel = Number(label);
                  if (isNaN(numLabel)) return String(label);
                  return timePeriod === "month"
                    ? getShortMonthName(numLabel)
                    : `Q${numLabel}`;
                } else {
                  // For multiple years or all years with quarterly/monthly view
                  const numLabel = Number(label);
                  if (!isNaN(numLabel)) {
                    const isYearId = years.some((y) => y.id === numLabel);
                    if (isYearId) {
                      const year = chartYearLabel(years.find((y) => y.id === numLabel)?.year);
                      return year ? `${year}` : `${label}`;
                    }
                  }
                  return String(label);
                }
              }}
            />
            <Legend
              verticalAlign="top"
              align="center"
              wrapperStyle={{
                fontSize: isMobile ? 10 : 13, // Reduce legend font size for mobile and desktop
                paddingRight: 0,
                paddingTop: 0,
                paddingBottom: 10,
              }}
            />
            {/* Create a line for each unique variable */}
            {uniqueVariables.map((variable, index) => (
              <Line
                key={variable.id}
                type="monotone"
                dataKey={variable.name}
                stroke={getColorForId(variable.id)}
                strokeWidth={2}
                activeDot={{ r: 6 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Expanded view dialog */}
      <Dialog
        open={isExpanded}
        onClose={() => setIsExpanded(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "95vw",
            height: "90vh",
            maxWidth: "none",
            margin: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <span>
            {category.charAt(0).toUpperCase() + category.slice(1)} - All
            Variables Trend
          </span>
          <IconButton
            onClick={() => setIsExpanded(false)}
            size="small"
            sx={{ color: "grey.600" }}
          >
            <FullscreenExitIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 1 }}>
          {/* Expanded chart with larger height and enhanced x-axis display */}
          <ResponsiveContainer width="100%" height={window.innerHeight - 200}>
            <LineChart
              data={chartData}
              margin={{
                top: 20,
                right: (() => {
                  const expandedXAxisConfig = getOptimalXAxisConfig(
                    chartData.length,
                    timePeriod,
                    true,
                  );
                  return expandedXAxisConfig.angle !== 0 ? 60 : 40;
                })(),
                left: mode === "amount" || mode === "average" ? 80 : 60,
                bottom: (() => {
                  const expandedXAxisConfig = getOptimalXAxisConfig(
                    chartData.length,
                    timePeriod,
                    true,
                  );
                  return expandedXAxisConfig.height + 20;
                })(),
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="displayPeriod"
                axisLine={false}
                tick={{
                  fontSize: (() => {
                    const expandedXAxisConfig = getOptimalXAxisConfig(
                      chartData.length,
                      timePeriod,
                      true,
                    );
                    return expandedXAxisConfig.fontSize;
                  })(),
                  fontFamily: "Segoe UI",
                }}
                tickLine={false}
                type="category"
                angle={(() => {
                  const expandedXAxisConfig = getOptimalXAxisConfig(
                    chartData.length,
                    timePeriod,
                    true,
                  );
                  return expandedXAxisConfig.angle;
                })()}
                textAnchor={(() => {
                  const expandedXAxisConfig = getOptimalXAxisConfig(
                    chartData.length,
                    timePeriod,
                    true,
                  );
                  return expandedXAxisConfig.textAnchor;
                })()}
                height={(() => {
                  const expandedXAxisConfig = getOptimalXAxisConfig(
                    chartData.length,
                    timePeriod,
                    true,
                  );
                  return expandedXAxisConfig.height;
                })()}
                interval={(() => {
                  const expandedXAxisConfig = getOptimalXAxisConfig(
                    chartData.length,
                    timePeriod,
                    true,
                  );
                  return expandedXAxisConfig.interval;
                })()}
                tickFormatter={(value) =>
                  getCompactLabel(value, timePeriod, chartData.length, true)
                }
              />
              <YAxis
                allowDecimals={mode === "count" ? false : true}
                domain={mode === "count" ? [0, "auto"] : [0, "auto"]}
                tickFormatter={(value) => {
                  if (mode === "amount" || mode === "average") {
                    const absValue = Math.abs(value);
                    if (absValue >= 1000000) {
                      return `$${(value / 1000000).toFixed(1)}M`;
                    } else if (absValue >= 1000) {
                      return `$${(value / 1000).toFixed(1)}K`;
                    } else {
                      return new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(value);
                    }
                  } else if (mode === "count") {
                    return value;
                  }
                  return value;
                }}
              />
              <Tooltip
                formatter={(value: number | string, name, props) => {
                  if (
                    (name === "Volume" && mode === "amount") ||
                    (name === "Average Price" && mode === "average")
                  ) {
                    return [
                      new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(value as number),
                      name,
                    ];
                  }
                  return [value, name];
                }}
                labelFormatter={(label) => {
                  // Use the displayPeriod from the data item directly
                  const dataItem = chartData.find(
                    (item) =>
                      item.period === label ||
                      item.displayPeriod === label ||
                      String(item.period) === String(label),
                  );

                  if (dataItem && dataItem.displayPeriod) {
                    return dataItem.displayPeriod;
                  }

                  // Fallback to original logic with better error handling
                  if (timePeriod === "year") {
                    const year = chartYearLabel(years.find(
                      (y) => y.id === Number(label),
                    )?.year);
                    return year ? `${year}` : `${label}`;
                  } else if (isSingleYearSelected) {
                    const numLabel = Number(label);
                    if (isNaN(numLabel)) return String(label);
                    return timePeriod === "month"
                      ? getShortMonthName(numLabel)
                      : `Q${numLabel}`;
                  } else {
                    // For multiple years or all years with quarterly/monthly view
                    const numLabel = Number(label);
                    if (!isNaN(numLabel)) {
                      const isYearId = years.some((y) => y.id === numLabel);
                      if (isYearId) {
                        const year = chartYearLabel(years.find((y) => y.id === numLabel)?.year);
                        return year ? `${year}` : `${label}`;
                      }
                    }
                    return String(label);
                  }
                }}
              />
              <Legend
                verticalAlign="top"
                align="left"
                wrapperStyle={{
                  paddingRight: 0,
                  paddingTop: 0,
                  paddingBottom: 20,
                }}
              />

              {uniqueVariables.map((variable, index) => (
                <Line
                  key={variable.id}
                  type="monotone"
                  dataKey={variable.name}
                  name={variable.name}
                  stroke={getColorForId(variable.id)}
                  strokeWidth={2}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </DialogContent>
      </Dialog>

      <DataExplanationDialog
        open={isOpen}
        onClose={closeExplanation}
        explanation={explanation}
        isLoading={isLoading}
        title={`${
          category.charAt(0).toUpperCase() + category.slice(1)
        } Trend Analysis`}
        dailyLimitInfo={dailyLimitInfo}
      />
    </>
  );
};

export default AllVariablesTrendChart;
